import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { type FC } from 'react';
import BaseTableNew from 'components/atoms/base-table/new';
import {
  CONDITIONS_PER_PAGE,
  useGetConditions,
} from 'infrastructure/hooks/ccm';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import IcdCodeIcon from 'components/atoms/icons/icd-code';
import { useLocalStorage } from 'infrastructure/hooks/utils';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import classNames from 'classnames';
import { useCheckPatientModule } from 'infrastructure/hooks/patient/use-check-patient-module';

import s from './styles.module.scss';
import { columns } from './columns';
import PatientConditionModalForm from '../admin-panel/forms/patient-condition';
import { useCondition } from './use-condition';
import ExpandButton from '../expand-button';
import ConditionsInfoTooltip from '../conditions/info-tooltip';

interface ConditionGoalsProps {
  patientGuid: string;
  isSingle?: boolean;
  disableActions?: boolean;
  withoutDivider?: boolean;
}

const ConditionGoals: FC<ConditionGoalsProps> = ({
  patientGuid,
  isSingle,
  disableActions = false,
  withoutDivider = false,
}) => {
  const {
    open,
    page,
    goals,
    isLoading,
    modifyItemGuid,
    addLoading,
    editLoading,
    deleteLoading,
    Alert,
    conditionForUpdate,
    documentationTimeSort,
    setPage,
    setOpen,
    handleDelete,
    toggleOpen,
    onSubmit,
    setConditionForUpdate,
    onCancelForm,
    setDocumentationTimeSort,
  } = useCondition({ patientGuid });
  const { t } = useTranslation();
  const { value, setValue } = useLocalStorage(
    'conditions-table-opened-state',
    true,
  );
  const { goals: conditions } = useGetConditions({
    patientGuid,
    meta: { items: 1000, page: 1 },
  });
  const { isBlockCcmModule, isBlockRpmModule } =
    useCheckPatientModule(patientGuid);

  const handleSetValue = () => {
    if (isSingle) return;
    setValue(!value);
  };

  const id = isSingle ? undefined : 'conditions';
  const disableActionButton =
    disableActions || (isBlockCcmModule && isBlockRpmModule);

  return (
    <>
      <div
        className={classNames(s.wrapper, {
          [s.single]: isSingle,
          [s['without-divider']]: withoutDivider,
        })}
        id={id}
        data-open={value}
      >
        <div className={s['title-wrapper']}>
          <h3
            className={s.title}
            data-cy="conditions-section"
            onClick={handleSetValue}
          >
            {!isSingle && <IcdCodeIcon />}
            {t('labels.conditions')}
            {!isSingle && (
              <ConditionsInfoTooltip conditions={conditions?.items} />
            )}
          </h3>
          <div className={s.actions}>
            <BaseButton
              className={s['add-condition-btn']}
              label={`+ ${t('labels.addCondition')}`}
              dataCy="condition-goals-add-btn"
              onClick={() => setOpen(true)}
              disabled={disableActionButton || isLoading || addLoading}
            />
            {!isSingle && (
              <ExpandButton
                expanded={value!}
                onClick={handleSetValue}
                dataCy="toggle-conditions-btn"
              />
            )}
          </div>
        </div>
        <CollapseAnimation
          opened={value!}
          className={s.collapse}
          disableAnimation={isSingle}
        >
          <div className={s.divider} />
          <BaseTableNew
            items={goals?.items ?? []}
            columns={columns({
              setConditionForUpdate,
              toggleOpen,
              handleDelete,
              loading: editLoading || deleteLoading,
              updatedItemGuid: modifyItemGuid,
              disableActions: disableActionButton,
              documentationTimeSort,
            })}
            bordered
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: CONDITIONS_PER_PAGE,
              total: goals?.count ?? 0,
              position: ['bottomCenter'],
            }}
            onChangePage={(newPage) => {
              setPage(newPage);
            }}
            dataCy="conditions-table"
            emptyText={
              <EmptyTablePlaceholder text={t('labels.noConditionsLogged')} />
            }
            onSort={(field, order) => {
              setDocumentationTimeSort(order);
            }}
          />
        </CollapseAnimation>
      </div>
      <PatientConditionModalForm
        title={
          conditionForUpdate
            ? t('labels.editCondition')
            : t('labels.addCondition')
        }
        loading={addLoading || editLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
        open={open}
        defaultValues={conditionForUpdate}
      />
      {Alert}
    </>
  );
};
export default ConditionGoals;
